import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { map, Observable, Subject, take } from 'rxjs';
import { RouterModule } from '@angular/router';
import { DashboardHeaderComponent } from '@app/layout/components/dashboard-header/dashboard-header.component';
import { DashboardType } from '@app/core/enums/dashboard-types.enum';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SharedModule } from '@app/shared/shared.module';
import { provideNgxMask } from 'ngx-mask';
import { CanComponentDeactivate } from '@app/shared/guards/unsaved-changes.guard';
import { User } from '@app/core/interfaces/user.interface';
import { AdminService } from '../../services/admin.service';
import { UserDetailsService } from '@app/shared/services/userDetails.service';
import { TokenStorageService } from '@app/core/services/token-storage.service';
import { AccountTypes } from '@app/shared/enums/accountTypes.enum';
import { ToastService } from '@app/core/services/toast.service';
import { SaveResult } from '@app/shared/interfaces/saveResult.interface';
import { LoadSpinnerWrapperComponent } from '@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component';
import {
  ChangePasswordModalComponent
} from '@app/shared/interfaces/change-password-modal/change-password-modal.component';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-admin-account-preferences',
  providers: [
    provideNgxMask()
  ],
  imports: [
    RouterModule,
    DashboardHeaderComponent,
    MatCardModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatDialogModule,
    SharedModule
],
  templateUrl: './admin-account-preferences.component.html',
  styleUrl: './admin-account-preferences.component.scss'
})
export class AdminAccountPreferencesComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  ngDestroy$ = new Subject();
  dashboardType = DashboardType.Admin;
  accountForm!: FormGroup;
  initialAccountFormValues: any;
  userData!: User;
  loading = false;

  constructor(
    private fb: FormBuilder,
    private userDetailsService: UserDetailsService,
    private tokenService: TokenStorageService,
    private toastService: ToastService,
    private adminService: AdminService,
    private dialog: MatDialog,
  ) {
  }

  ngOnInit(): void {
    this.accountForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [{ value: '', disabled: true }, [Validators.required, Validators.email]]
    });

    this.initialAccountFormValues = this.accountForm.getRawValue();

    const userId = this.tokenService.getEffectiveUser()?.id;
    if (!userId) {
      this.toastService.showErrorToast('User ID not found.');
      return;
    }
    this.loading = true;
    this.userDetailsService.getUserDetails$(userId, AccountTypes.ADMIN)
      .subscribe((user: User) => {
        this.loading = false;
        if (!user) {
          this.toastService.showErrorToast('User details not found.');
          return;
        }
        this.userData = user;
        this.accountForm.patchValue({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email
        });

        this.initialAccountFormValues = this.accountForm.getRawValue();
    });
  }

  onPreferencesFormSubmit() {
    if (this.accountForm.valid) {
      this.loading = true;
      const updatedUserInfo = {
        ...this.userData,
        firstName: this.accountForm.get('firstName')?.value,
        lastName: this.accountForm.get('lastName')?.value
      };
      this.adminService.updateAdminUserAccountSettings(this.userData.subId!, updatedUserInfo)
        .subscribe((response: SaveResult) => {
          this.loading = false;
          if (response.errorMessages?.length > 0) {
            this.toastService.showErrorToast('Something went wrong. Please try again.');
          } else {
            this.accountForm.markAsPristine();
            this.toastService.showSuccessToast('Account settings updated.');
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next(true);
  }

  canDeactivate(): boolean | Observable<boolean> {
    if (!this.accountForm.dirty) {
      return true;
    }

    let confirmed = false;
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Unsaved Changes';
    dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to leave this page?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => confirmed = true);
    return dialog.afterClosed().pipe(map(() => confirmed));
  }

  onPreferencesCancel() {
    this.confirmAndReset(this.accountForm, this.initialAccountFormValues);
  }

  openChangePasswordModal() {
    this.dialog.open(ChangePasswordModalComponent, {});
  }

  private confirmAndReset(form: FormGroup, initialValues: any) {
    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Unsaved Changes';
    dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to cancel?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => {
        form.reset(initialValues);
        form.markAsPristine();
      });
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.accountForm?.dirty) {
      event.preventDefault();
      event.returnValue = ''; // required for modern browsers
      return '';
    }
    return undefined;
  }
}
